import type { ReactNode } from 'react';
import { Num, Plural, T, useGT } from 'gt-react';
import type { WorkbookResult, WorkbookColumnarSheet } from '@erp/shared-types';
import { Button } from '@/components/ui/button';
import { sortKeysLabel, type AppliedRowSteps } from './worksheetRowView';

/**
 * The output grid's summary line, which is also its control: each part says
 * what the grid shows and offers a link that flips it. The filters part shows
 * only when the Result has filter counts, the errors part only when the
 * Result has Error cells, the sort part only when the Worksheet has a sort,
 * and a link only when the Input-order preview can serve the other view.
 */
export function WorksheetRowSummary({
  result,
  sheet,
  applied,
  setApplied,
}: {
  result: WorkbookResult;
  sheet: WorkbookColumnarSheet;
  /** The steps the grid applies, with a step the Worksheet lacks counted as applied. */
  applied: AppliedRowSteps;
  setApplied: (applied: AppliedRowSteps) => void;
}) {
  const gt = useGT();
  const switchable = result.inputOrderRows !== undefined;
  return (
    <div
      role="group"
      aria-label={gt('Rows shown', {
        context: 'worksheet result row toggles',
      })}
      className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground tabular-nums"
    >
      <FiltersPart
        result={result}
        switchable={switchable && sheet.filters.length > 0}
        applied={applied.filters}
        setApplied={(filters) => setApplied({ ...applied, filters })}
      />
      {Boolean(result.errorCount) && (
        <>
          <span aria-hidden="true">·</span>
          <ErrorsPart count={result.errorCount ?? 0} />
        </>
      )}
      {sheet.sort.length > 0 && (
        <>
          <span aria-hidden="true">·</span>
          <SortPart
            sort={sheet.sort}
            switchable={switchable}
            applied={applied.sort}
            setApplied={(sort) => setApplied({ ...applied, sort })}
          />
        </>
      )}
    </div>
  );
}

function FiltersPart({
  result,
  switchable,
  applied,
  setApplied,
}: {
  result: WorkbookResult;
  switchable: boolean;
  applied: boolean;
  setApplied: (applied: boolean) => void;
}) {
  const gt = useGT();
  const kept = result.rowCount;
  // Without filters every row is kept, so there is nothing to split.
  const counts = result.filterCounts?.filters.length
    ? result.filterCounts
    : undefined;
  if (!counts) {
    return (
      <Status>
        <T context="worksheet result row count">
          <Num>{kept}</Num> <Plural n={kept} one="row" other="rows" />
        </T>
      </Status>
    );
  }
  const total = counts.inputRowCount;
  const removed = total - kept;
  if (!removed) {
    return (
      <Status>
        <T context="worksheet result rows kept, none filtered out">
          <Num>{kept}</Num> of <Num>{total}</Num>{' '}
          <Plural n={total} one="row" other="rows" /> · none filtered out
        </T>
      </Status>
    );
  }
  return (
    <>
      <Status>
        {applied ? (
          <T context="worksheet result rows kept and filtered out">
            <Num>{kept}</Num> of <Num>{total}</Num>{' '}
            <Plural n={total} one="row" other="rows" /> · <Num>{removed}</Num>{' '}
            filtered out
          </T>
        ) : (
          <T context="worksheet result all rows shown, filtered-out rows grayed">
            All <Num>{total}</Num> <Plural n={total} one="row" other="rows" /> ·{' '}
            <Num>{removed}</Num> filtered out, grayed
          </T>
        )}
      </Status>
      {switchable && (
        <SummaryLink
          label={
            applied
              ? gt('Show filtered-out rows', {
                  context: 'worksheet summary link: show filtered-out rows',
                })
              : gt('Hide filtered-out rows', {
                  context: 'worksheet summary link: hide filtered-out rows',
                })
          }
          onClick={() => setApplied(!applied)}
        >
          {applied ? (
            <T context="worksheet summary link: show filtered-out rows">Show</T>
          ) : (
            <T context="worksheet summary link: hide filtered-out rows">Hide</T>
          )}
        </SummaryLink>
      )}
    </>
  );
}

/** The Result's Error cells, which the export writes as error values too. */
function ErrorsPart({ count }: { count: number }) {
  const gt = useGT();
  return (
    <span
      className="text-destructive"
      title={gt('Cells showing an error value, such as #N/A', {
        context: 'worksheet summary tooltip: what the error count counts',
      })}
    >
      <T context="worksheet result error cell count">
        <Num>{count}</Num> <Plural n={count} one="error" other="errors" />
      </T>
    </span>
  );
}

function SortPart({
  sort,
  switchable,
  applied,
  setApplied,
}: {
  sort: WorkbookColumnarSheet['sort'];
  switchable: boolean;
  applied: boolean;
  setApplied: (applied: boolean) => void;
}) {
  const gt = useGT();
  const keys = sortKeysLabel(sort);
  const sortedByAll = gt('Sorted by {keys}', {
    context: 'worksheet summary: rows in sort order',
    keys: keys.full,
  });
  return (
    <>
      <Status>
        {applied ? (
          <span title={keys.short === keys.full ? undefined : sortedByAll}>
            {gt('Sorted by {keys}', {
              context: 'worksheet summary: rows in sort order',
              keys: keys.short,
            })}
          </span>
        ) : (
          <T context="worksheet summary: rows in input order">Original order</T>
        )}
      </Status>
      {switchable &&
        (applied ? (
          <SummaryLink
            label={gt('Show original order', {
              context: 'worksheet summary link: show rows in input order',
            })}
            onClick={() => setApplied(false)}
          />
        ) : (
          <SummaryLink
            label={gt('Apply sort', {
              context: 'worksheet summary link: show rows in sort order',
            })}
            title={gt('Sort by {keys}', {
              context: 'worksheet summary link tooltip: the sort to apply',
              keys: keys.full,
            })}
            onClick={() => setApplied(true)}
          />
        ))}
    </>
  );
}

/** Announces the part's new state after its link flips it. */
function Status({ children }: { children: ReactNode }) {
  return (
    <span role="status" aria-live="polite">
      {children}
    </span>
  );
}

/** A link-styled button; `label` is its accessible name and, without children, its text. */
function SummaryLink({
  label,
  title,
  onClick,
  children,
}: {
  label: string;
  title?: string;
  onClick: () => void;
  children?: ReactNode;
}) {
  return (
    <Button
      type="button"
      variant="link"
      size="sm"
      className="h-auto p-0 text-xs"
      aria-label={children ? label : undefined}
      title={title}
      onClick={onClick}
    >
      {children ?? label}
    </Button>
  );
}
